import React, {useId, useState} from 'react';
import {IconButton, Menu, MenuItem, Stack, Tooltip} from '@mui/material';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import PetsOutlinedIcon from '@mui/icons-material/PetsOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import FlightOutlinedIcon from '@mui/icons-material/FlightOutlined';
import SportsSoccerOutlinedIcon from '@mui/icons-material/SportsSoccerOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import TagIcon from '@mui/icons-material/Tag';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import {emojiCategories, SkinTone, skinTones} from './EmojiOptions';

const categoryIcons: Record<string, typeof EmojiEmotionsOutlinedIcon> = {
    'smileys-emotion': EmojiEmotionsOutlinedIcon,
    'people-body': PeopleOutlineIcon,
    component: ExtensionOutlinedIcon,
    'animals-nature': PetsOutlinedIcon,
    'food-drink': RestaurantOutlinedIcon,
    'travel-places': FlightOutlinedIcon,
    activities: SportsSoccerOutlinedIcon,
    objects: LightbulbOutlinedIcon,
    symbols: TagIcon,
    flags: FlagOutlinedIcon,
};

export default function EmojiPickerToolbar({activeCategory, onCategoryClick, skinTone, onSkinToneChange}: {
    activeCategory?: string,
    onCategoryClick: (categoryId: string) => void,
    skinTone: SkinTone,
    onSkinToneChange: (tone: SkinTone) => void,
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const menuId = useId();

    return (
        <Stack direction="row" flexWrap="wrap" gap={1} role="group" aria-label="Emoji categories and skin tone">
            {emojiCategories.map(category => {
                const Icon = categoryIcons[category.id];
                return (
                    <Tooltip key={category.id} title={category.name}>
                        <IconButton
                            size="small"
                            aria-label={`Go to ${category.name}`}
                            aria-pressed={activeCategory === category.id}
                            color={activeCategory === category.id ? 'primary' : 'default'}
                            sx={{width: 28, height: 30}}
                            onClick={() => onCategoryClick(category.id)}
                        >
                            <Icon sx={{fontSize: 20}}/>
                        </IconButton>
                    </Tooltip>
                );
            })}
            <Tooltip title="Choose skin tone">
                <IconButton
                    size="small"
                    aria-label="Choose skin tone"
                    aria-haspopup="menu"
                    aria-expanded={Boolean(anchorEl)}
                    aria-controls={anchorEl ? menuId : undefined}
                    onClick={event => setAnchorEl(event.currentTarget)}
                    sx={{fontSize: 20, width: 30, height: 30}}
                >
                    {skinTones.find(option => option.tone === skinTone)?.emoji}
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                slotProps={{list: {id: menuId, 'aria-label': 'Skin tone'}}}
            >
                {skinTones.map(option => (
                    <MenuItem
                        key={option.tone}
                        role="menuitemradio"
                        aria-label={option.name}
                        aria-checked={skinTone === option.tone}
                        selected={skinTone === option.tone}
                        onClick={() => {
                            onSkinToneChange(option.tone);
                            setAnchorEl(null);
                        }}
                    >
                        <Stack component="span" direction="row" gap={1} alignItems="center">
                            <span aria-hidden="true">{option.emoji}</span>
                        </Stack>
                    </MenuItem>
                ))}
            </Menu>
        </Stack>
    );
}
